import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchArchivedData, removeArchivedList, resetArchivedCards, handleArchiveList, pushArchivedList } from '../../../redux/Slices/cardSlice';
import ArchivedCard from '../../Workspaces/ArchivedCard';
import Spinner from '../../Global/Spinner';
import './scss/Archived.scss';
import useHandlePopoverClick from '../../../hooks/useHandlePopoverClick';
import { showSuccess, showError } from '../../../utils/snackbarHelpers';
import { config } from '../../../config';
import { Refresh, Delete } from '../../icons';

const Archived = ({ boardId, setBoard, className = '', isInNav = false }) => {
    const [activeTab, setActiveTab] = useState('cards');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [isFocused, setIsFocused] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);

    const [debouncedQuery, setDebouncedQuery] = useState('');

    const { archivedCards, archivedLists, cardsCount, listsCount, hasMore, status } = useSelector((state) => state.card);
    const dispatch = useDispatch();
    const observer = useRef();
    const isFirstRender = useRef(true);
    const contentRef = useRef(null);

    const { handlePopoverClick } = useHandlePopoverClick();

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const timer = setTimeout(() => {
            setDebouncedQuery(searchTerm);
            setPage(1);
            dispatch(resetArchivedCards()); // Reset archivedCards on search
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm, dispatch]);

    useEffect(() => {
        dispatch(fetchArchivedData({ boardId, activeTab, page, debouncedQuery }));
    }, [boardId, activeTab, page, debouncedQuery, dispatch]);

    // This effect is now redundant as the one above covers it
    // useEffect(() => {
    //     if (archivedCards.length < 1 && activeTab === 'cards') {
    //         dispatch(fetchArchivedData({ boardId, activeTab, page, debouncedQuery }));
    //     }
    //     if (archivedLists.length < 1 && activeTab === 'lists') {
    //         dispatch(fetchArchivedData({ boardId, activeTab, page, debouncedQuery }));
    //     }
    // }, [activeTab]);

    const lastCardRef = useCallback((node) => {
        if (status === 'loading') return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore) {
                setPage((prevPage) => prevPage + 1);
            }
        }, {
            root: contentRef.current,
            threshold: 0.1
        });

        if (node) observer.current.observe(node);
    }, [hasMore, status]);

    // const handleDeleteList = (listId) => setBoard((prev) => ({
    //     ...prev,
    //     actionLists: prev.actionLists.filter((list) => list._id !== listId),
    // }));


    // Archive the action list
    const handleListSendToBoard = async (listId, archivedStatus) => {
        if (isArchiving) return;
        setIsArchiving(true);
        
        const listToUpdate = archivedLists.find(l => l._id === listId);

        try {
            // Optimistic update - if archiving (though here we mostly unarchive from this screen)
            if (archivedStatus && listToUpdate) {
                dispatch(pushArchivedList({ ...listToUpdate, archived: true }));
            }

            const data = await dispatch(handleArchiveList({ boardId, listId, archivedStatus })).unwrap();
            const { actionList } = data; // Extract the updated action list

            // Update board state
            setBoard((prevBoard) => {
                // If unarchiving, add to board. If archiving, remove from board.
                // Note: handleArchiveList handles the list status.
                
                const listExists = prevBoard.actionLists.some(l => l._id === actionList._id);
                
                if (!archivedStatus) {
                    // Restoring to board
                    return {
                        ...prevBoard,
                        actionLists: listExists 
                            ? prevBoard.actionLists.map(l => l._id === actionList._id ? actionList : l)
                            : [...prevBoard.actionLists, actionList]
                    };
                } else {
                    // Archiving (removing from board)
                    return {
                        ...prevBoard,
                        actionLists: prevBoard.actionLists.filter(l => l._id !== listId)
                    };
                }
            });

            showSuccess(dispatch, archivedStatus ? 'List archived successfully!' : 'List restored successfully!');
        } catch (error) {
            showError(dispatch, 'Error updating the list: ' + error.message);
        } finally {
            setIsArchiving(false);
        }
    };


    return (

        <div className={`archived-container ${className}`}>

            {
                isInNav && (
                    <div className="menu-header">
                        <h3 className="menu-title">Archived items</h3>
                    </div>
                )
            }
            <div className="tabs">
                <button className={activeTab === 'cards' ? 'active' : ''} onClick={() => setActiveTab('cards')}>
                    Cards {cardsCount ? `(${cardsCount})` : ''}
                </button>
                <button className={activeTab === 'lists' ? 'active' : ''} onClick={() => setActiveTab('lists')}>
                    Lists {listsCount ? `(${listsCount})` : ''}
                </button>
            </div>
            <div className={`search-box${isFocused ? ' focused' : ''}`}>
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                />
            </div>
            <div className="content" ref={contentRef}>
                {
                    <>
                        {activeTab === 'cards' ? (
                            archivedCards?.length > 0 ? (
                                <ol className="archived-cards">
                                    {archivedCards.map((card, index) => {
                                        const isLastCard = index === archivedCards.length - 1;
                                        return (
                                            <li ref={isLastCard ? lastCardRef : null} key={card._id}>
                                                <ArchivedCard card={card} setBoard={setBoard} />
                                            </li>
                                        );
                                    })}
                                </ol>
                            ) : (
                                <>
                                    {
                                        status !== 'loading' && (
                                            <p className="empty-message" style={{ textAlign: 'center' }}>Nothing found</p>
                                        )
                                    }
                                </>

                            )
                        ) : (
                            archivedLists?.length > 0 ? (
                                <div className="lists">
                                    {archivedLists.map((list, index) => {
                                        const isLastList = index === archivedLists.length - 1;
                                        return (
                                            <div key={list._id} className="list-item-wrapper" ref={isLastList ? lastCardRef : null}>
                                                <div className="zoobbe-card">
                                                    <h2 className="card-title" title={list.title}>{list.title}</h2>
                                                </div>
                                                <div className='card-action-wrapper archive-actions-wrapper'>
                                                    <div className="send-to-board-btn" onClick={() => handleListSendToBoard(list._id, false)} data-tooltip-content="Send to board" data-tooltip-position="top">
                                                        {isArchiving ? (
                                                            <Spinner size={14} color="var(--brand-color)" speed={1.5} strokeWidth={4} />
                                                        ) : (
                                                            <Refresh size={18} />
                                                        )}
                                                    </div>

                                                    <div
                                                        className="delete-archive-list-btn"
                                                        id={'popover-delete-archived-list-' + list._id}
                                                        onClick={(e) => handlePopoverClick(e, 'deleteAction', { type: 'ACTIONLIST', listId: list._id, id: boardId })}
                                                        data-tooltip-content="Delete"
                                                        data-tooltip-position="top"
                                                        data-popover-trigger
                                                    >
                                                        <Delete size={18} />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <>
                                    {
                                        status !== 'loading' && (
                                            <p className="empty-message" style={{ textAlign: 'center' }}>Nothing found</p>
                                        )
                                    }
                                </>
                            )
                        )}
                    </>
                }

                {status === 'loading' && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'start', height: '80px', marginTop: '25px' }}>
                        <Spinner size={30} color="#3498db" speed={1.4} />
                    </div>
                )}
            </div>

        </div>
    );
};

export default Archived;
